<template>
  <base-page-layout title="Faixas de preços">
    <template v-if="!hasGeneratedPriceRange && !loading" #custom-filters>
      <div class="d-flex justify-end pr-4" style="gap: 15px">
        <mf-button v-if="showBackToAnalyseButton" outlined label="Voltar para análise" @click="discardChangesAndGoBackToAnalyse" />
        <mf-button v-if="!hasGeneratedPriceRange && priceRanges.length" label="Gerar análise" @click="generateAnalyse" />
      </div>
      <analyze-filter
        :selected-campaign.sync="selectedCampaign"
        :filter-quantity="0"
        :filters="filters"
        @clear-filters="clearFilters"
        @new-search="newSearch"
      />
    </template>
    <v-scroll-y-transition>
      <v-row v-if="showPriceRangeSlider && !hasGeneratedPriceRange">
        <v-col>
          <price-range-slider
            :values.sync="sliderValues"
            :maximum-value="maximumValue"
            :minimum-value="minimumValue"
            :minimum-price="minimumPrice"
            @apply-new-prices="checkProductRangeChange"
          />
        </v-col>
      </v-row>
    </v-scroll-y-transition>
    <mf-loading :boolean-loader="loading" loader-type="dialog">
      <price-range-container
        v-if="priceRanges && priceRanges.length"
        :price-ranges="priceRanges"
        :filters="appliedFilters"
        :range-count="Number(appliedFilters.range_count)"
        :loading.sync="loading"
        :title.sync="name"
        :description.sync="description"
        :has-generated-price-range="hasGeneratedPriceRange"
        :error-name="errorName"
        :error-description="errorDescription"
        @upsert-analyse="upsertPriceRangeAnalytic"
        @generate-analyse="generateAnalyse"
        @cancel-analyse="cancelAnalyse"
      />
      <mf-empty-card v-else title="Nova análise" :has-image="!showInvalidFiltersMessage">
        <template v-if="showInvalidFiltersMessage" #content-card>
          <p class="texto-empty-card-wrong-filters py-12">
            Não existem dados a serem exibidos para essa combinação de filtros. Tente mudar suas seleções ou entre em contato com o suporte pelo chat.
          </p>
        </template>

        <template v-else #content-card>
          <p class="texto-empty-card">
            Dentro dos <span class="filter-text">filtros</span> você consegue selecionar os produtos que quer analisar, assim como a quantidade de faixas que
            quer gerar.
          </p>
        </template>
      </mf-empty-card>
    </mf-loading>
    <products-without-price-modal v-model="openProductsWithoutSalesModal" :products="products_without_sales" @confirm-insert="insertProductsWithPrice" />
  </base-page-layout>
</template>

<script>
import {
  QUERY_PRICE_RANGES_ANALYTIC,
  QUERY_PRODUCTS_WITHOUT_SALES,
  QUERY_GENERATE_PRICE_RANGES_HISTOGRAM,
  QUERY_GENERATE_PRICE_RANGES_ANALYTICS,
  MUTATION_UPSERT_PRICE_RANGE_ANALYTICS,
  QUERY_PRODUCTS_BY_SKUS,
} from '../../graphql'
import { QUERY_SALES_CAMPAIGN_BY_ID } from '@/modules/campanhas/gestao-ofertas-v2/graphql'
import { QUERY_GET_GENERIC_CAMPAIGNS_BY_ID } from '@/modules/campanhas/views/components/graphql'
import { deepDelete } from '@/helpers/deepDelete'
import AnalyzeFilter from '@/components/AnalyzeFilter.vue'
import BasePageLayout from '@/components/BasePageLayout.vue'
import PriceRangeSlider from '../components/PriceRangeSlider.vue'
import ProductsWithoutPriceModal from '../components/ProductsWithoutPriceModal.vue'
import PriceRangeContainer from '../components/PriceRangeContainer.vue'
import { required, minLength } from 'vuelidate/lib/validators'
import { notOnlySpace } from '@/helpers/validators.js'
import { priceRangeMixin } from '../../mixins/price-range-mixin'
import cloneDeep from 'lodash/cloneDeep'

export default {
  name: 'NewAnalyse',
  components: {
    AnalyzeFilter,
    BasePageLayout,
    PriceRangeSlider,
    ProductsWithoutPriceModal,
    PriceRangeContainer,
  },
  mixins: [priceRangeMixin],
  provide: function () {
    return {
      rangeChanged: this.rangeChanged,
    }
  },
  data: () => ({
    loading: false,
    showPriceRangeSlider: false,
    openProductsWithoutSalesModal: false,
    minimumValue: 0,
    maximumValue: 0,
    minimumPrice: 0,
    hasGeneratedPriceRange: false,
    hasProductRangeChanged: false,
    _id: '',
    name: '',
    description: '',
    filters: {
      reference_period_start: '',
      reference_period_end: '',
      period_start: '',
      period_end: '',
      products: [],
      units: [],
      range_count: 2,
      campaign: {
        _id: '',
        type: '',
      },
    },
    appliedFilters: {},
    sliderValues: [5, 6],
    range_count: 0,
    products_without_sales: [],
    products_with_sales: [],
    priceRanges: [],
    originalPriceRanges: [],
    showInvalidFiltersMessage: false,
    selectedCampaign: {},
  }),
  computed: {
    showBackToAnalyseButton() {
      return !!this.$route.params.id && !!this.priceRanges
    },
    errorName() {
      if (!this.$v.name) return ''
      if (this.$v.name.$error) {
        if (!this.$v.name.required) return 'Dê um nome para esta análise.'
        else if (!this.$v.name.minLength) return 'O título possui menos de 2 caracteres.'
        else if (!this.$v.name.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      } else return ''
      return ''
    },
    errorDescription() {
      if (!this.$v.description) return ''
      if (this.$v.description.$error) {
        if (!this.$v.description.required) return 'Informe detalhes para melhor identificação da análise.'
        else if (!this.$v.description.minLength) return 'A descrição possui menos de 2 caracteres.'
        else if (!this.$v.description.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      } else return ''
      return ''
    },
    upsertPriceRangeAnalyticData() {
      const analytic = {
        name: this.name,
        description: this.description,
        price_ranges: this.priceRanges,
        filters: {
          ...this.filters,
          range_count: Number(this.filters.range_count),
          products: this.filters.products.map((product) => (product.sku ? product.sku : product)),
          units: this.filters.units,
          campaign: this.filters.campaign?._id ? this.filters.campaign : null,
        },
      }
      return this._id ? { ...analytic, _id: this._id } : analytic
    },
    generateAnalyseData() {
      const { reference_period_start, reference_period_end, period_start, period_end, units, range_count } = this.filters
      deepDelete(this.priceRanges, ['__typename'])
      return {
        reference_period_start,
        reference_period_end,
        period_start,
        period_end,
        range_count: Number(range_count),
        units,
        price_ranges: this.priceRanges.map((range) => {
          return {
            ...range,
            price_range_metric: {
              percentil_50: range.price_range_metric.percentil_50,
              min_price: range.price_range_metric.min_price,
              max_price: range.price_range_metric.max_price,
            },
            products_metrics: range.products_metrics.map((productMetrics) => {
              return {
                product: productMetrics.product,
                price_range_metric: {
                  percentil_20: productMetrics.price_range_metric.percentil_20,
                  percentil_80: productMetrics.price_range_metric.percentil_80,
                },
              }
            }),
          }
        }),
      }
    },
  },
  async mounted() {
    const id = this.$route.params.id
    if (id) {
      this._id = id
      await this.getPriceRangeAnalytic()
    }
  },
  methods: {
    clearFilters() {
      this.showPriceRangeSlider = false
      this.filters = {
        reference_period_start: '',
        reference_period_end: '',
        period_start: '',
        period_end: '',
        products: [],
        range_count: 2,
      }
      this.products_without_sales = []
      this.products_with_sales = []
      this.priceRanges = []
    },
    insertProductsWithPrice(products) {
      this.openProductsWithoutSalesModal = false
      this.products_without_sales = [...products]
      this.filters.products = [...this.products_with_sales, ...this.products_without_sales]
      this.generatePriceRangesHistogram()
    },
    async getPriceRangeAnalytic() {
      try {
        this.loading = true
        const {
          data: { priceRangesAnalytics },
        } = await this.$apollo.query({
          query: QUERY_PRICE_RANGES_ANALYTIC,
          variables: {
            _id: this._id,
          },
          fetchPolicy: 'no-cache',
        })
        this.name = priceRangesAnalytics.name
        this.description = priceRangesAnalytics.description
        this.filters = {
          ...priceRangesAnalytics.filters,
          products: await this.getProducts(priceRangesAnalytics.filters.products),
        }
        this.appliedFilters = cloneDeep(this.filters)
        this.priceRanges = priceRangesAnalytics.price_ranges
        this.hasGeneratedPriceRange = true
        if (this.filters.campaign) await this.getCampaign()
        this.getPriceRangeValues()
      } catch (error) {
        console.log(error)
      } finally {
        this.loading = false
      }
    },
    async getCampaign() {
      const { _id, type } = this.filters.campaign
      if (!_id) return
      if (type === 'OFFER_SCHEDULE') {
        const {
          data: { offerSchedule },
        } = await this.$apollo.query({
          query: QUERY_SALES_CAMPAIGN_BY_ID,
          variables: { id: _id },
          context: {
            uri: this.$microservicesUrls['sales'],
          },
          fetchPolicy: 'no-cache',
        })
        this.selectedCampaign = offerSchedule
      } else {
        const {
          data: {
            genericCampaigns: { campaigns },
          },
        } = await this.$apollo.query({
          query: QUERY_GET_GENERIC_CAMPAIGNS_BY_ID,
          fetchPolicy: 'network-only',
          variables: {
            campaignIds: [_id],
          },
          context: {
            uri: this.$microservicesUrls['campaigns'],
          },
        })
        this.selectedCampaign = campaigns[0]
      }
    },
    async getProducts(skus) {
      try {
        const {
          data: { products },
        } = await this.$apollo.query({
          query: QUERY_PRODUCTS_BY_SKUS,
          variables: {
            skus,
          },
          fetchPolicy: 'no-cache',
        })
        return products.products
      } catch (error) {
        console.log(error)
      }
      return []
    },
    async newSearch() {
      this.products_without_sales = []
      this.products_with_sales = []
      this.appliedFilters = cloneDeep(this.filters)
      try {
        this.loading = true
        this.showPriceRangeSlider = false
        const { reference_period_start, reference_period_end, products, units } = this.filters
        const skus = products.map((product) => product.sku)
        const {
          data: { productsWithoutSales },
        } = await this.$apollo.query({
          query: QUERY_PRODUCTS_WITHOUT_SALES,
          variables: {
            reference_period_start,
            reference_period_end,
            products: skus,
            units,
          },
          fetchPolicy: 'no-cache',
        })
        if (productsWithoutSales && productsWithoutSales?.products?.length) {
          this.products_without_sales = productsWithoutSales.products
          this.openProductsWithoutSalesModal = true

          const productWithSales = this.filters.products.filter((product) => {
            return !this.products_without_sales.some((productWithoutSale) => productWithoutSale.sku === product.sku)
          })
          this.products_with_sales = [...productWithSales]
        } else {
          this.products_with_sales = [...this.filters.products]
          this.products_without_sales = []
          this.generatePriceRangesHistogram()
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.loading = false
      }
    },
    async generatePriceRangesHistogram() {
      this.showInvalidFiltersMessage = false
      try {
        this.loading = true
        const { reference_period_start, reference_period_end, period_start, period_end, units, range_count } = this.filters
        const products = this.products_with_sales.map((product) => {
          return { sku: product.sku, name: product.name }
        })
        const products_without_sales = this.products_without_sales.map((product) => {
          return { _id: product._id, sku: product.sku, name: product.name, price: product.price }
        })
        const { data: generatePriceRangesHistogram } = await this.$apollo.query({
          query: QUERY_GENERATE_PRICE_RANGES_HISTOGRAM,
          variables: {
            reference_period_start,
            reference_period_end,
            period_start,
            period_end,
            range_count: Number(range_count),
            units,
            products,
            products_without_sales,
          },
          fetchPolicy: 'no-cache',
        })
        this.filters.range_count = Number(range_count)
        this.priceRanges = generatePriceRangesHistogram.generatePriceRangesHistogram
        if (!this.priceRanges && !this.priceRanges.length) {
          this.showInvalidFiltersMessage = true
          return
        }
        this.getPriceRangeValues()
      } catch (error) {
        if (!this.filters.products.length && !this.products_without_sales.length) {
          this.showInvalidFiltersMessage = true
          this.priceRanges = []
          return
        }
        console.log(error)
      } finally {
        this.loading = false
      }
    },
    getPriceRangeValues() {
      this.sliderValues = this.priceRanges.map((range) => {
        const {
          price_range_metric: { max_price },
        } = range
        return max_price
      })

      this.getMinAndMaxValues()
    },
    getMinAndMaxValues() {
      this.minimumPrice = this.priceRanges.at(0).price_range_metric.min_price
      let formatMin = this.priceRanges.at(0).price_range_metric.min_price
      let formatMax = this.sliderValues.at(-1)

      if (formatMin % 1 !== 0) {
        formatMin = Math.floor((formatMin + Number.EPSILON) * 10) / 10
      }
      if (formatMax % 1 !== 0) {
        formatMax = Math.round((formatMax + Number.EPSILON) * 10) / 10
      }

      this.maximumValue = formatMax
      this.minimumValue = formatMin
      this.showPriceRangeSlider = true
    },
    async checkProductRangeChange() {
      let confirmed = true
      if (this.hasProductRangeChanged) {
        confirmed = await this.$confirm({
          message:
            'Alguns produtos foram alterados de faixa manualmente, ao aplicar os novos preços eles podem sofrer alteração de acordo com os novos valores das faixas.',
          confirmText: 'CONTINUAR',
          cancelText: 'CANCELAR',
        })
          .then(() => true)
          .catch(() => false)
      }

      if (confirmed === false) return

      this.applyNewPrices()
    },
    applyNewPrices() {
      let allProducts = []

      this.priceRanges.forEach((value, index) => {
        value.price_range_metric.max_price = this.sliderValues[index]
        value.price_range_metric.min_price = this.sliderValues[index - 1] ?? this.minimumPrice
        const {
          price_range_metric: { max_price, min_price },
        } = value
        value.price_range_metric.percentil_50 = (max_price + min_price) / 2

        allProducts.push(...value.products_metrics)
      })

      const newPriceRanges = this.priceRanges.map((priceRange, index) => {
        const filteredProducts = allProducts.filter((product) => {
          const {
            price_range_metric: { min_price, max_price },
          } = priceRange

          if (index === 0) {
            return product.product.price <= max_price && product.product.price >= min_price
          }

          return product.product.price <= max_price && product.product.price > min_price
        })

        if (filteredProducts.length) {
          priceRange.products_metrics = filteredProducts
        } else {
          priceRange.products_metrics = []
        }

        return priceRange
      })

      this.hasProductRangeChanged = false
      this.priceRanges = newPriceRanges
    },
    async generateAnalyse() {
      try {
        this.loading = true
        this.showPriceRangeSlider = false
        const {
          data: { generatePriceRangesAnalytics },
        } = await this.$apollo.query({
          query: QUERY_GENERATE_PRICE_RANGES_ANALYTICS,
          variables: this.generateAnalyseData,
          fetchPolicy: 'no-cache',
        })
        this.hasGeneratedPriceRange = true
        this.priceRanges = generatePriceRangesAnalytics
      } catch (error) {
        console.log(error)
      } finally {
        this.loading = false
      }
    },
    async upsertPriceRangeAnalytic() {
      this.$v.$reset()
      this.$v.$touch()
      if (this.$v.$error) {
        this.$snackbar({ message: 'Preencha todos os campos corretamente', snackbarColor: 'error' })
        this.$nextTick(() => this.$vuetify.goTo(document.querySelector('.error--text'), { offset: 200 }))
        return
      }

      const data = this.upsertPriceRangeAnalyticData
      deepDelete(this.upsertPriceRangeAnalyticData, ['__typename'])
      try {
        this.loading = true
        await this.$apollo.mutate({
          mutation: MUTATION_UPSERT_PRICE_RANGE_ANALYTICS,
          variables: {
            data,
          },
        })
        const message = this._id ? 'Análise editada com sucesso.' : 'Análise criada com sucesso.'
        this.$snackbar({ message, snackbarColor: 'success' })
        this.forcePush = true
        this.$router.push({ path: '/produtos/faixas-precos/' })
      } catch (error) {
        console.log(error)
      } finally {
        this.loading = false
      }
    },
    backToAnalyze() {
      this.hasGeneratedPriceRange = false
    },
    cancelAnalyse() {
      this.hasGeneratedPriceRange = false
      this.originalPriceRanges = cloneDeep(this.priceRanges)
      this.showPriceRangeSlider = true
      this.getPriceRangeValues()
      window.scrollTo(0, 0)
    },
    discardChangesAndGoBackToAnalyse() {
      this.hasGeneratedPriceRange = true
      this.priceRanges = cloneDeep(this.originalPriceRanges)
      this.originalPriceRanges = []
      this.showPriceRangeSlider = true
      this.getPriceRangeValues()
    },
    rangeChanged() {
      this.hasProductRangeChanged = true
    },
  },
  validations() {
    return {
      name: {
        required,
        minLength: minLength(2),
        notOnlySpace,
      },
      description: {
        required,
        minLength: minLength(2),
        notOnlySpace,
      },
    }
  },
}
</script>

<style lang="scss">
.texto-empty-card {
  width: 480px;
  height: 87px;
  left: 467px;
  top: 707px;

  /* Medium-number regular */
  font-family: 'Lato';
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 120%;
  /* or 26px */
  text-align: center;
  letter-spacing: 0.01em;

  /* Cinza / D30 Título */
  color: $cinza-d10;
}
.filter-text {
  font-weight: 900;
  color: $marca-1;
}

.texto-empty-card-wrong-filters {
  max-width: 400px;
  font-size: 16px;
  color: $cinza-l50;
}
</style>
